<script setup lang="ts">
import SuggestionTabBar from './SuggestionTabBar.vue';
import SearchForms from '@components/ui/index&view&widget/forms/SearchForms.vue';
import { LoadingStatus } from '@itinerary/ItineraryModuleTypes';

defineProps<{
    fixFormToTop?: boolean;
}>();
const emit = defineEmits<{
    changeSuggestions: [args: boolean];
    changeFixed: [fixed: boolean];
    forceReload: [];
}>();
const itineraryStore = useItineraryStore();
const currentModule = useCurrentItineraryModule();
const searchFormStore = useSearchFormStore();
const { haveSuggestions } = storeToRefs(searchFormStore);
const suggestions = ref<InstanceType<typeof SuggestionTabBar>>();
const wrapper = ref<HTMLDivElement>();
const fixedSuggestions = ref(false);
const fixed = ref(false);
const lastScrollTop = ref(0);
const loading = computed(
    () =>
        itineraryStore.loading ||
        currentModule.value.currentLoadingStatus === LoadingStatus.None
);
const {
    open: show,
    close: closeBottomSheet,
    isOpened: showBottomSheet,
} = useClosable('change-search');
onUnmounted(() => window.removeEventListener('scroll', onscroll));
const onForceReload = () => {
    closeBottomSheet();
    emit('forceReload');
};
const hideSuggestions = computed(
    () =>
        !showBottomSheet.value &&
        fixed.value &&
        !fixedSuggestions.value &&
        haveSuggestions.value
);
const onscroll = () => {
    const nowScrollTop = window.scrollY;
    const headerHeight = document.getElementById('__header')?.clientHeight ?? 0;
    const headerPassed = nowScrollTop > headerHeight;
    const suggestionsPassed =
        nowScrollTop >
        headerHeight + (suggestions.value?.$el.clientHeight ?? 0);
    if (lastScrollTop.value > nowScrollTop) {
        fixed.value = headerPassed;
        fixedSuggestions.value = haveSuggestions.value && headerPassed;
    } else {
        fixed.value = suggestionsPassed;
        fixedSuggestions.value = haveSuggestions.value && !suggestionsPassed;
    }
    lastScrollTop.value = nowScrollTop;
};
watch(fixedSuggestions, v => emit('changeSuggestions', v));
watch(fixed, v => emit('changeFixed', v));
onMounted(() => {
    window.addEventListener('scroll', onscroll);
});

defineExpose({ show });
</script>

<template>
    <div
        ref="wrapper"
        class="change-search-wrapper"
        :class="{ 'with-suggestions': haveSuggestions }"
    >
        <div
            class="change-search-container"
            :class="{
                fixed: !showBottomSheet && fixed,
                'hide-suggestions': hideSuggestions,
                'fix-forms': fixFormToTop,
            }"
        >
            <SuggestionTabBar
                v-if="haveSuggestions"
                ref="suggestions"
                class="suggestions"
            />
            <div class="search-forms-wrapper">
                <SearchForms
                    class="search-forms"
                    :class="{ show: showBottomSheet }"
                    is-change-search
                    :loading
                    @force-reload="onForceReload"
                />
            </div>
            <div
                class="bottom-sheet-container"
                :class="{ show: showBottomSheet }"
                @click="closeBottomSheet()"
            />
        </div>
    </div>
</template>

<style lang="scss" scoped>
.change-search-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 76px;
    background: white;
    &.with-suggestions {
        height: 128px;
    }
}
.search-forms-wrapper {
    width: 100%;
    height: 76px;
    @include mobile-only {
        height: fit-content;
    }
    display: flex;
    align-items: center;
    justify-content: center;
    @include shadow(1);
}
.change-search-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    background: white;
    z-index: 10000;
    position: absolute;
    top: 0;
    &.fixed {
        -webkit-transform: translateZ(0);
        -ms-transform: translateZ(0);
        transform: translateZ(0);
        position: fixed;
    }

    &.hide-suggestions {
        transform: translateY(-52px);
    }
    &.fix-forms {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        z-index: 10000000000;
        .suggestions {
            display: none;
        }
    }
}

.search-forms {
    @include shadow(0, true);
    border-radius: 0 !important;
    padding: 14px 0 !important;
}

.bottom-sheet-container {
    display: none;
}

@media (min-width: $app-desktop-min-width) {
    .change-search-container {
        &.fix-forms {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            z-index: 10000000000;
            .suggestions {
                display: none;
            }
        }
    }
}

@media (max-width: $app-mobile-max-width) {
    .change-search-container {
        z-index: 1000085;
    }
    .change-search-wrapper {
        height: 0;
        &.with-suggestions {
            height: 56px;
        }
    }
    .bottom-sheet-container {
        display: none;
        z-index: 1000000;
        &.show {
            display: flex;
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            background: rgba($color: usePalette(), $alpha: 0.8);
        }
    }

    .search-forms {
        visibility: hidden;
        position: fixed;
        bottom: 0;
        z-index: 10000000;
        transform: translateY(110%);
        padding: 20px 0 0 0 !important;
        &.show {
            transition:
                visibility 0s linear 200ms,
                transform 200ms linear;
            visibility: visible;
            transform: unset;
            transition-delay: 0s;
        }
    }
}
</style>
